import {
  OrganisationFeatureFlags,
  OrganisationLink,
  OrganisationPermission,
} from '@core/entities/organisation/types'
import { Entity } from '@core/entities/entity'
import { IOrganisation } from '@core/entities/organisation/IOrganisation'
import { plainToInstance } from 'class-transformer'

export class Organisation extends Entity<IOrganisation> {
  id?: number
  name: string
  logoUrl: string
  isEnabled: boolean
  links: OrganisationLink[]
  permittedActions: OrganisationPermission[]
  isVisibleTestOrganisation: boolean
  timezone: string
  featureFlags: OrganisationFeatureFlags

  public static new(payload: unknown): Organisation {
    const entity = plainToInstance(Organisation, payload)

    return entity
  }

  private hasPermission(permission: OrganisationPermission): boolean {
    return this.permittedActions.includes(permission)
  }

  public canChangeUserRole(): boolean {
    return this.hasPermission('UPDATE_MEMBER_ROLE')
  }

  public canChangeUserStatus(): boolean {
    return this.hasPermission('UPDATE_MEMBER_STATUS')
  }

  public isSuperUser(rootRole: string): boolean {
    const allowedRoles = ['SUPER_ADMIN', 'SUPPORT_USER']
    return allowedRoles.includes(rootRole)
  }

  public canSeeLinkedOrganizations(rootRole: string): boolean {
    return this.isSuperUser(rootRole)
  }

  public canAddUser(): boolean {
    return this.hasPermission('INVITE_USER')
  }

  public canListTemplates(): boolean {
    return this.hasPermission('LIST_TEMPLATES')
  }

  public canViewTimeline(): boolean {
    return this.hasPermission('LIST_AUDITS')
  }

  public canCreateProjects(): boolean {
    return this.hasPermission('CREATE_PROJECTS')
  }

  public canSeeBusinessUnits(): boolean {
    return this.hasPermission('LIST_BUSINESS_UNITS')
  }

  public canCreateBusinessUnits(): boolean {
    return this.hasPermission('CREATE_BUSINESS_UNITS')
  }

  public canSeeAllPermits(): boolean {
    return this.hasPermission('SEE_ALL_PERMITS')
  }

  public canEdit(): boolean {
    return this.hasPermission('EDIT')
  }

  public canCreatePermitTemplates(): boolean {
    return this.hasPermission('CREATE_TEMPLATES')
  }

  public canCreateTaskTemplates(): boolean {
    return this.hasPermission('CREATE_TASK_TEMPLATES')
  }

  public canCreateChecklistTemplates(): boolean {
    return this.hasPermission('CREATE_CHECKLIST_TEMPLATES')
  }

  public canCreateInspectionTemplates(): boolean {
    return this.hasPermission('CREATE_INSPECTION_TEMPLATES')
  }

  public canCreateRegisterTemplates(): boolean {
    return this.hasPermission('CREATE_REGISTER_TEMPLATES')
  }

  public canEditPermitTemplateFolder(): boolean {
    return this.hasPermission('EDIT_FOLDER_PERMIT_TEMPLATE')
  }

  public canEditChecklistTemplateFolder(): boolean {
    return this.hasPermission('EDIT_FOLDER_CHECKLIST_TEMPLATE')
  }

  public canEditInspectionTemplateFolder(): boolean {
    return this.hasPermission('EDIT_FOLDER_INSPECTION_TEMPLATE')
  }

  public canEditTaskTemplateFolder(): boolean {
    return this.hasPermission('EDIT_FOLDER_TASK_TEMPLATE')
  }

  public canEditProcoreFolder(): boolean {
    return this.hasPermission('EDIT_FOLDER_PROCORE')
  }

  public canSeeAuthorisationGroups(): boolean {
    return this.hasPermission('LIST_GROUPS')
  }

  public canEditAuthorisationGroups(): boolean {
    return this.hasPermission('CREATE_GROUPS')
  }

  public canManageApiKeys(): boolean {
    return this.hasPermission('MANAGE_API_KEYS')
  }

  public canViewDocumentEngineTemplates(): boolean {
    return this.hasPermission('VIEW_DOCUMENT_ENGINE_TEMPLATES')
  }

  public canManageDocumentEngineTemplates(): boolean {
    return this.hasPermission('MANAGE_DOCUMENT_ENGINE_TEMPLATES')
  }

  public canSeeDeveloperSection(): boolean {
    return (
      this.hasPermission('VIEW_API_KEYS') ||
      this.hasPermission('MANAGE_API_KEYS')
    )
  }

  public canSeeOrganisationAuthenticationConfig(): boolean {
    return this.hasPermission('MFA_UPDATE')
  }

  public canSeeOrganisationSSOConfig(): boolean {
    const allPermissions: OrganisationPermission[] = [
      'CREATE_IDENTITY_PROVIDER',
      'DELETE_IDENTITY_PROVIDER',
      'ADD_IDENTITY_PROVIDER_USERS',
      'REMOVE_IDENTITY_PROVIDER_USERS',
      'RESET_IDENTITY_PROVIDER_USERS',
      'UPDATE_FULL_ROLLOUT',
    ]
    return allPermissions.some((permission) => this.hasPermission(permission))
  }

  public canManageCompanies(): boolean {
    return this.hasPermission('MANAGE_COMPANIES')
  }

  public canEditUserCompany(): boolean {
    return this.hasPermission('EDIT_USER_COMPANY')
  }

  public canSetFeatureFlags(): boolean {
    return this.hasPermission('SET_FEATURE_FLAGS')
  }

  public canExportUsers(): boolean {
    return this.hasPermission('LIST_MEMBERS')
  }

  public canListCompanies(): boolean {
    return this.hasPermission('LIST_COMPANIES')
  }

  public canReadCompanyOnboardingState(): boolean {
    return this.hasPermission('READ_COMPANY_ONBOARDING_STATE')
  }

  public canSubmitCompanyOnboardingForm(): boolean {
    return this.hasPermission('SUBMIT_COMPANY_ONBOARDING_FORM')
  }

  public canUpdateCompanyOnboardingForm(): boolean {
    return this.hasPermission('UPDATE_COMPANY_ONBOARDING_FORM')
  }
}
